import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useAdmin } from '../../context/AdminContext';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  BarChart2, Users, Baby, FileText, Search, Download, LogOut, Eye,
  Award, Shield, AlertTriangle, Activity, Droplets, QrCode, Trash2, X, ExternalLink, Info, UserPlus,
} from '../../components/Icons';
import { getStoolInfo } from '../../utils/stoolInfo';
import logo from '../../assets/logo.png';
import styles from './AdminDashboard.module.css';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';

// interpretasi total skor (maks 43) — proporsional, label "perkiraan"
function scoreInterp(total) {
  const t = total || 0;
  if (t <= 11) return { label: 'Rendah', cls: styles.toneGood };
  if (t <= 25) return { label: 'Sedang', cls: styles.toneWarn };
  return { label: 'Tinggi', cls: styles.toneDanger };
}

// Warna grafik = nilai token (recharts tak bisa baca CSS var)
  const C = { teal: '#0a5252', tealMid: '#2f7a6e', tealLight: '#cfe3db',
    orange: '#e27830', red: '#dc2626', green: '#2f7a6e', blue: '#2563eb' };
  const PIE_COLORS = [C.teal, C.tealMid, C.orange, C.blue, C.red, C.tealLight];
  // Label persentase DI DALAM irisan pie (tidak terpotong di tepi kartu).
  // Sembunyikan label untuk irisan sangat kecil (<6%) agar tidak menumpuk.
  const renderInsideLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.06) return null;
  const RAD = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.6;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
      fontSize={12} fontWeight={700}>
      {(percent * 100).toFixed(0)}%
    </text>
  );
};

function AdminDashboard() {
  const { admin, logout, isSuperAdmin } = useAdmin();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({ users: 0, children: 0, logs: 0, screenings: 0 });
  const [children, setChildren] = useState([]);
  const [logs, setLogs] = useState([]);
  const [screenings, setScreenings] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [qrChild, setQrChild] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const qrCardRef = useRef(null);

  const downloadQR = async (child) => {
    if (!qrCardRef.current) return;
    try {
      const canvas = await html2canvas(qrCardRef.current, { backgroundColor: '#ffffff', scale: 3, useCORS: true });
      const link = document.createElement('a');
      link.download = `NOWGUT_QR_${child.name.replace(/\s+/g, '_')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      window.alert('Download gagal. Coba lagi.');
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const { data: childData } = await supabase.from('children').select('*').order('created_at', { ascending: false });
    const { data: logData } = await supabase.from('gi_logs').select('*, children(name)').order('date', { ascending: false });
    const { data: screenData } = await supabase.from('screenings').select('*, children(name)').order('created_at', { ascending: false });
    const { data: adminData } = await supabase.from('admins').select('*').order('created_at', { ascending: false });

    const uniqueCaregivers = new Set(childData?.map(c => c.caregiver_id) || []);
    setChildren(childData || []);
    setLogs(logData || []);
    setScreenings(screenData || []);
    setAdmins(adminData || []);
    setStats({
      users: uniqueCaregivers.size,
      children: childData?.length || 0,
      logs: logData?.length || 0,
      screenings: screenData?.length || 0,
    });
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleLogout = async () => { await logout(); navigate('/admin/login'); };

  // BUG FIX: URL dinamis (bukan hardcode nowgut.com)
  const getChildUrl = (childId) => `${window.location.origin}/child-summary/${childId}`;

  const handleDeleteChild = async (id) => {
    if (!isSuperAdmin) return;
    if (!window.confirm('Hapus data anak ini beserta semua log-nya?')) return;
    await supabase.from('gi_logs').delete().eq('child_id', id);
    await supabase.from('screenings').delete().eq('child_id', id);
    await supabase.from('children').delete().eq('id', id);
    fetchAll();
  };

  const handleDeleteAdmin = async (adminId, adminRole) => {
    if (!isSuperAdmin) return;
    if (adminRole === 'super_admin') { window.alert('Super Admin tidak bisa dihapus.'); return; }
    if (!window.confirm('Hapus admin ini?')) return;
    await supabase.from('admins').delete().eq('id', adminId);
    fetchAll();
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const childSheet = XLSX.utils.json_to_sheet(children.map(c => ({
      'Nama': c.name, 'Usia': c.age, 'Jenis Kelamin': c.gender,
      'Berat (kg)': c.weight_kg, 'Tinggi (cm)': c.height_cm, 'Diagnosis': c.diagnosis,
      'Komorbid': c.comorbid_condition ? 'Ya' : 'Tidak',
      'Penggunaan Obat': c.medication_use ? 'Ya' : 'Tidak',
      'ASD Sedang-Berat': c.moderate_severe_autism ? 'Ya' : 'Tidak',
      'Verbal Terbatas': c.limited_verbal ? 'Ya' : 'Tidak',
      'Sosial Rendah': c.low_social_interaction ? 'Ya' : 'Tidak',
      'Diet Ketat': c.restricted_diet ? 'Ya' : 'Tidak',
      'Tanggal Daftar': new Date(c.created_at).toLocaleDateString('id-ID'),
    })));
    XLSX.utils.book_append_sheet(wb, childSheet, 'Data Anak');
    const logSheet = XLSX.utils.json_to_sheet(logs.map(l => ({
      'Nama Anak': l.children?.name || '-', 'Tanggal': l.date,
      'Nafsu Pagi': l.appetite_morning || '-', 'Makanan Pagi': l.food_morning || '-',
      'Porsi Pagi': l.portion_morning || '-', 'Cairan Pagi': l.fluid_morning || '-',
      'Nafsu Siang': l.appetite_noon || '-', 'Makanan Siang': l.food_noon || '-',
      'Porsi Siang': l.portion_noon || '-', 'Cairan Siang': l.fluid_noon || '-',
      'Nafsu Malam': l.appetite_night || '-', 'Makanan Malam': l.food_night || '-',
      'Porsi Malam': l.portion_night || '-', 'Cairan Malam': l.fluid_night || '-',
      'Frekuensi BAB': l.stool_frequency || '-', 'Bentuk Feses (Bristol)': l.stool_type || '-',
      'Warna Feses': l.stool_color || '-', 'Bau': l.stool_smell || '-',
      'BAK': l.urination || '-', 'Keluhan': l.stool_complaint || '-',
    })));
    XLSX.utils.book_append_sheet(wb, logSheet, 'GI Log');
    const screenSheet = XLSX.utils.json_to_sheet(filteredScreenings.map(s => ({
      'Nama Anak': s.children?.name || '-',
      'Tanggal': new Date(s.created_at).toLocaleDateString('id-ID'),
      'Total Skor': s.score || '-',
    })));
    XLSX.utils.book_append_sheet(wb, screenSheet, 'Hasil Skrining');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }),
      `NOWGUT_Data_${new Date().toLocaleDateString('id-ID').replace(/\//g, '-')}.xlsx`);
  };

  const filteredChildren = children.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()));
  const filteredLogs = logs.filter(l => l.children?.name?.toLowerCase().includes(search.toLowerCase()));
  const filteredScreenings = screenings.filter(s => s.children?.name?.toLowerCase().includes(search.toLowerCase()));

  // ===== DATA GRAFIK =====
  const bristolDist = [1,2,3,4,5,6,7].map(t => ({
    name: `Tipe ${t}`,
    jumlah: logs.filter(l => l.stool_type === t).length,
  }));
  const appetiteData = ['Baik', 'Kurang', 'Menolak'].map(a => ({
    name: a,
    Pagi: logs.filter(l => l.appetite_morning === a).length,
    Siang: logs.filter(l => l.appetite_noon === a).length,
    Malam: logs.filter(l => l.appetite_night === a).length,
  }));
  const colorDist = ['Kuning', 'Coklat', 'Hitam', 'Pucat'].map(c => ({
    name: c, value: logs.filter(l => l.stool_color === c).length,
  })).filter(c => c.value > 0);
  const riskData = [
    { name: 'Komorbid', value: children.filter(c => c.comorbid_condition).length },
    { name: 'Obat-obatan', value: children.filter(c => c.medication_use).length },
    { name: 'ASD Berat', value: children.filter(c => c.moderate_severe_autism).length },
    { name: 'Verbal Terbatas', value: children.filter(c => c.limited_verbal).length },
    { name: 'Sosial Rendah', value: children.filter(c => c.low_social_interaction).length },
    { name: 'Diet Ketat', value: children.filter(c => c.restricted_diet).length },
  ].filter(r => r.value > 0);
  const smellData = [
    { name: 'Normal', value: logs.filter(l => l.stool_smell === 'Normal').length },
    { name: 'Menyengat', value: logs.filter(l => l.stool_smell === 'Menyengat').length },
  ].filter(d => d.value > 0);
  const urinationData = [
    { name: 'Lancar', value: logs.filter(l => l.urination === 'Lancar').length },
    { name: 'Tidak Lancar', value: logs.filter(l => l.urination === 'Tidak Lancar').length },
  ].filter(d => d.value > 0);

  const tabs = [
    { key: 'overview', label: 'Overview', icon: <BarChart2 size={15} aria-hidden="true" /> },
    { key: 'charts', label: 'Visualisasi', icon: <Eye size={15} aria-hidden="true" /> },
    { key: 'children', label: 'Data Anak', icon: <Baby size={15} aria-hidden="true" /> },
    { key: 'logs', label: 'GI Log', icon: <FileText size={15} aria-hidden="true" /> },
    { key: 'screenings', label: 'Skrining', icon: <Search size={15} aria-hidden="true" /> },
    ...(isSuperAdmin ? [{ key: 'admins', label: 'Kelola Admin', icon: <Users size={15} aria-hidden="true" /> }] : []),
  ];

  return (
    <div className={styles.page}>
      {/* SIDEBAR */}
      <div className={styles.sidebar}>
        <div className={styles.sidebarLogo}>
          <img src={logo} alt="NOWGUT" className={styles.logoImg} />
          <div>
            <div className={styles.logoName}>NOWGUT</div>
            <div className={styles.logoTagline}>Admin Panel</div>
          </div>
        </div>

        <div className={styles.adminInfo}>
          <div className={styles.adminAvatar}>
            {isSuperAdmin
              ? <Award size={24} color="#fde68a" aria-hidden="true" />
              : <Shield size={24} color="#cfe3db" aria-hidden="true" />}
          </div>
          <div className={styles.adminName}>{admin?.name}</div>
          <div className={styles.adminEmail}>{admin?.email}</div>
          <div className={`${styles.roleBadge} ${isSuperAdmin ? styles.roleSuper : styles.roleAdmin}`}>
            {isSuperAdmin
              ? <><Award size={12} aria-hidden="true" /> Super Admin</>
              : <><Shield size={12} aria-hidden="true" /> Admin</>}
          </div>
        </div>

        <nav className={styles.nav}>
          {tabs.map(tab => (
            <button key={tab.key}
              className={`${styles.navItem} ${activeTab === tab.key ? styles.navItemActive : ''}`}
              onClick={() => {
                setActiveTab(tab.key);
                if (tab.key !== 'children' && tab.key !== 'logs' && tab.key !== 'screenings') setSearch('');
              }}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className={styles.sidebarBottom}>
          <button className={styles.exportBtn} onClick={exportToExcel}>
            <Download size={15} aria-hidden="true" /> Export Excel
          </button>
          <button className={styles.logoutBtn} onClick={handleLogout}>
            <LogOut size={15} aria-hidden="true" /> Logout
          </button>
        </div>
      </div>

      {/* MAIN */}
      <div className={styles.main}>
        <div className={styles.topBar}>
          <div>
            <h1 className={styles.pageTitle}>{tabs.find(t => t.key === activeTab)?.label}</h1>
            {!isSuperAdmin && (
              <p className={styles.accessNote}>
                <Shield size={13} aria-hidden="true" />
                Mode Admin — akses baca & export. Hubungi Super Admin untuk menghapus data.
              </p>
            )}
          </div>
          {(activeTab === 'children' || activeTab === 'logs' || activeTab === 'screenings') && (
            <input className={styles.searchInput} placeholder="Cari nama anak..."
              value={search} onChange={e => setSearch(e.target.value)} />
          )}
        </div>

        {successMsg && <div className={styles.successMsg}>{successMsg}</div>}

        {loading ? (
          <div className={styles.loading}>Memuat data...</div>
        ) : (
          <>
            {/* Placeholder — diisi di Langkah B & C */}
            {/* OVERVIEW */}
            {activeTab === 'overview' && (
              <div>
                <div className={styles.statGrid}>
                  {[
                    { icon: <Users size={26} aria-hidden="true" />, label: 'Total Caregiver', value: stats.users, tone: C.teal },
                    { icon: <Baby size={26} aria-hidden="true" />, label: 'Total Anak', value: stats.children, tone: C.blue },
                    { icon: <FileText size={26} aria-hidden="true" />, label: 'Total GI Log', value: stats.logs, tone: C.green },
                    { icon: <Search size={26} aria-hidden="true" />, label: 'Total Skrining', value: stats.screenings, tone: C.orange },
                  ].map(s => (
                    <div key={s.label} className={styles.statCard} style={{ '--tone': s.tone }}>
                      <div className={styles.statIcon}>{s.icon}</div>
                      <div className={styles.statValue}>{s.value}</div>
                      <div className={styles.statLabel}>{s.label}</div>
                    </div>
                  ))}
                </div>

                <div className={styles.card}>
                  <div className={styles.cardTitle}><FileText size={16} aria-hidden="true" /> GI Log Terbaru</div>
                  <table className={styles.table}>
                    <thead>
                      <tr>{['Nama Anak', 'Tanggal', 'Bentuk Feses', 'Frekuensi', 'Bau', 'BAK'].map(h => (
                        <th key={h} className={styles.th}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {logs.slice(0, 8).map(log => {
                        const info = log.stool_type ? getStoolInfo(log.stool_type, true) : null;
                        return (
                          <tr key={log.id} className={styles.tr}>
                            <td className={styles.td}>{log.children?.name || '-'}</td>
                            <td className={styles.td}>{log.date}</td>
                            <td className={styles.td}>
                              {log.stool_type ? `Tipe ${log.stool_type}` : '-'}
                              {info && <span className={`${styles.badge} ${info.tone === 'danger' ? styles.toneDanger : info.tone === 'warn' ? styles.toneWarn : styles.toneGood}`}> · {info.label}</span>}
                            </td>
                            <td className={styles.td}>{log.stool_frequency ?? '-'}x</td>
                            <td className={styles.td}>
                              <span className={log.stool_smell === 'Menyengat' ? styles.toneDanger : styles.toneGood}>
                                {log.stool_smell || '-'}
                              </span>
                            </td>
                            <td className={styles.td}>
                              <span className={log.urination === 'Tidak Lancar' ? styles.toneDanger : styles.toneGood}>
                                {log.urination || '-'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* VISUALISASI */}
            {activeTab === 'charts' && (
              <div>
                <p className={styles.chartSubtitle}>
                  Data divisualisasikan dari {logs.length} GI Log dan {children.length} profil anak.
                </p>

                <div className={styles.chartRow}>
                  <div className={styles.chartCard}>
                    <div className={styles.chartTitle}><Activity size={16} aria-hidden="true" /> Distribusi Bentuk Feses (Skala Bristol)</div>
                    <p className={styles.chartDesc}>Tipe 3–5 normal (4 ideal). Tipe 1–2 cenderung sembelit, 6–7 cenderung diare.</p>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={bristolDist}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5efec" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="jumlah" name="Jumlah" radius={[6,6,0,0]}>
                          {bristolDist.map((_, i) => (
                            <Cell key={i} fill={i <= 1 ? C.red : i <= 4 ? C.green : C.orange} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className={styles.chartCard}>
                    <div className={styles.chartTitle}><Activity size={16} aria-hidden="true" /> Nafsu Makan per Waktu</div>
                    <p className={styles.chartDesc}>Perbandingan nafsu makan pagi, siang, malam.</p>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={appetiteData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5efec" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="Pagi" fill={C.teal} radius={[4,4,0,0]} />
                        <Bar dataKey="Siang" fill={C.tealMid} radius={[4,4,0,0]} />
                        <Bar dataKey="Malam" fill={C.tealLight} radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className={styles.chartRow}>
                  <div className={styles.chartCard}>
                    <div className={styles.chartTitle}><BarChart2 size={16} aria-hidden="true" /> Distribusi Warna Feses</div>
                    <p className={styles.chartDesc}>Coklat/Kuning normal; Hitam/Pucat perlu perhatian.</p>
                    {colorDist.length === 0 ? <p className={styles.noData}>Belum ada data</p> : (
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie data={colorDist} cx="50%" cy="50%" outerRadius={80} dataKey="value"
                            labelLine={false} label={renderInsideLabel}>
                            {colorDist.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                          <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>

                  <div className={styles.chartCard}>
                    <div className={styles.chartTitle}><AlertTriangle size={16} aria-hidden="true" /> Distribusi Faktor Risiko Anak</div>
                    <p className={styles.chartDesc}>Jumlah anak per faktor risiko teridentifikasi.</p>
                    {riskData.length === 0 ? <p className={styles.noData}>Belum ada data</p> : (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={riskData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5efec" />
                          <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={100} />
                          <Tooltip />
                          <Bar dataKey="value" name="Jumlah Anak" fill={C.teal} radius={[0,6,6,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                <div className={styles.chartRow}>
                  <div className={styles.chartCard}>
                    <div className={styles.chartTitle}><Activity size={16} aria-hidden="true" /> Distribusi Bau Feses</div>
                    {smellData.length === 0 ? <p className={styles.noData}>Belum ada data</p> : (
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={smellData} cx="50%" cy="50%" outerRadius={70} dataKey="value"
                            labelLine={false} label={renderInsideLabel}>
                            <Cell fill={C.green} /><Cell fill={C.red} />
                          </Pie>
                          <Tooltip />
                          <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>

                  <div className={styles.chartCard}>
                    <div className={styles.chartTitle}><Droplets size={16} aria-hidden="true" /> Kondisi BAK</div>
                    {urinationData.length === 0 ? <p className={styles.noData}>Belum ada data</p> : (
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={urinationData} cx="50%" cy="50%" outerRadius={70} dataKey="value"
                            labelLine={false} label={renderInsideLabel}>
                            <Cell fill={C.green} /><Cell fill={C.red} />
                          </Pie>
                          <Tooltip />
                          <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>

                  <div className={styles.chartCard}>
                    <div className={styles.chartTitle}><BarChart2 size={16} aria-hidden="true" /> Ringkasan Skrining</div>
                    <div className={styles.screeningSummary}>
                      {[
                        { label: 'Rendah', count: screenings.filter(s => (s.score||0) <= 11).length, color: C.green, bg: '#f0fdf4' },
                        { label: 'Sedang', count: screenings.filter(s => (s.score||0) > 11 && (s.score||0) <= 25).length, color: '#b45309', bg: '#fffbeb' },
                        { label: 'Tinggi', count: screenings.filter(s => (s.score||0) > 25).length, color: C.red, bg: '#fef2f2' },
                      ].map(s => (
                        <div key={s.label} className={styles.screeningItem} style={{ backgroundColor: s.bg }}>
                          <div className={styles.screeningCount} style={{ color: s.color }}>{s.count}</div>
                          <div className={styles.screeningLabel} style={{ color: s.color }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* DATA ANAK */}
            {activeTab === 'children' && (
              <div className={styles.card}>
                <div className={styles.cardTitle}><Baby size={16} aria-hidden="true" /> Data Anak ({filteredChildren.length})</div>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      {['Nama', 'Usia', 'Gender', 'BB', 'TB', 'Diagnosis', 'Faktor Risiko', 'QR',
                        ...(isSuperAdmin ? ['Hapus'] : [])].map(h => <th key={h} className={styles.th}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredChildren.map(child => {
                      const risks = [
                        child.comorbid_condition && 'Komorbid',
                        child.medication_use && 'Obat',
                        child.moderate_severe_autism && 'ASD Berat',
                        child.limited_verbal && 'Verbal',
                        child.low_social_interaction && 'Sosial',
                        child.restricted_diet && 'Diet',
                      ].filter(Boolean);
                      return (
                        <tr key={child.id} className={styles.tr}>
                          <td className={styles.td}><strong>{child.name}</strong></td>
                          <td className={styles.td}>{child.age_display || (child.age ? `${child.age} thn` : '-')}</td>
                          <td className={styles.td}>{child.gender || '-'}</td>
                          <td className={styles.td}>{child.weight_kg ? `${child.weight_kg} kg` : '-'}</td>
                          <td className={styles.td}>{child.height_cm ? `${child.height_cm} cm` : '-'}</td>
                          <td className={styles.td}>{child.diagnosis || '-'}</td>
                          <td className={styles.td}>
                            <div className={styles.riskTagsSmall}>
                              {risks.map(r => <span key={r} className={styles.riskTagSmall}>{r}</span>)}
                              {risks.length === 0 && <span className={styles.muted}>-</span>}
                            </div>
                          </td>
                          <td className={styles.td}>
                            <button className={styles.iconBtn} onClick={() => setQrChild(child)}>
                              <QrCode size={14} aria-hidden="true" /> QR
                            </button>
                          </td>
                          {isSuperAdmin && (
                            <td className={styles.td}>
                              <button className={`${styles.iconBtn} ${styles.deleteBtn}`} onClick={() => handleDeleteChild(child.id)}>
                                <Trash2 size={14} aria-hidden="true" />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* GI LOG */}
            {activeTab === 'logs' && (
              <div className={styles.card}>
                <div className={styles.cardTitle}><FileText size={16} aria-hidden="true" /> Semua GI Log ({filteredLogs.length})</div>
                <table className={styles.table}>
                  <thead>
                    <tr>{['Nama Anak', 'Tanggal', 'Nafsu Pagi', 'Nafsu Siang', 'Nafsu Malam', 'Bentuk Feses', 'Frek.', 'Warna', 'Bau', 'BAK'].map(h => (
                      <th key={h} className={styles.th}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map(log => {
                      const info = log.stool_type ? getStoolInfo(log.stool_type, true) : null;
                      return (
                        <tr key={log.id} className={styles.tr}>
                          <td className={styles.td}><strong>{log.children?.name || '-'}</strong></td>
                          <td className={styles.td}>{log.date}</td>
                          <td className={styles.td}>{log.appetite_morning || '-'}</td>
                          <td className={styles.td}>{log.appetite_noon || '-'}</td>
                          <td className={styles.td}>{log.appetite_night || '-'}</td>
                          <td className={styles.td}>
                            {log.stool_type ? `Tipe ${log.stool_type}` : '-'}
                            {info && <div className={`${styles.badge} ${info.tone === 'danger' ? styles.toneDanger : info.tone === 'warn' ? styles.toneWarn : styles.toneGood}`}>{info.label}</div>}
                          </td>
                          <td className={styles.td}>{log.stool_frequency ?? '-'}x</td>
                          <td className={styles.td}>{log.stool_color || '-'}</td>
                          <td className={styles.td}>
                            <span className={log.stool_smell === 'Menyengat' ? styles.toneDanger : styles.toneGood}>{log.stool_smell || '-'}</span>
                          </td>
                          <td className={styles.td}>
                            <span className={log.urination === 'Tidak Lancar' ? styles.toneDanger : styles.toneGood}>{log.urination || '-'}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* SKRINING */}
            {activeTab === 'screenings' && (
              <div className={styles.card}>
                <div className={styles.cardTitle}><Search size={16} aria-hidden="true" /> Hasil Skrining ({filteredScreenings.length})</div>
                <p className={styles.chartDesc}>
                  Interpretasi adalah perkiraan kasar dari total skor (maksimal 43). Rincian per instrumen dapat dilihat oleh caregiver di aplikasi.
                </p>
                <table className={styles.table}>
                  <thead>
                    <tr>{['Nama Anak', 'Tanggal', 'Total Skor', 'Interpretasi (perkiraan)'].map(h => (
                      <th key={h} className={styles.th}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {filteredScreenings.map(s => {
                      const interp = scoreInterp(s.score);
                      return (
                        <tr key={s.id} className={styles.tr}>
                          <td className={styles.td}><strong>{s.children?.name || '-'}</strong></td>
                          <td className={styles.td}>{new Date(s.created_at).toLocaleDateString('id-ID')}</td>
                          <td className={styles.td}><strong>{s.score ?? '-'}</strong></td>
                          <td className={styles.td}>
                            <span className={interp.cls} style={{ fontWeight: 600 }}>● {interp.label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* KELOLA ADMIN — hanya Super Admin */}
            {activeTab === 'admins' && isSuperAdmin && (
              <div>
                <div className={styles.card}>
                  <div className={styles.cardTitle}><Users size={16} aria-hidden="true" /> Daftar Admin ({admins.length})</div>
                  <table className={styles.table}>
                    <thead>
                      <tr>{['Nama', 'Email', 'Role', 'Bergabung', 'Aksi'].map(h => (
                        <th key={h} className={styles.th}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {admins.map(a => (
                        <tr key={a.id} className={styles.tr}>
                          <td className={styles.td}>
                            <strong>{a.name}</strong>
                            {a.id === admin?.id && <span className={styles.youBadge}>Anda</span>}
                          </td>
                          <td className={styles.td}>{a.email}</td>
                          <td className={styles.td}>
                            <span className={`${styles.roleBadge} ${a.role === 'super_admin' ? styles.roleSuper : styles.roleAdmin}`}>
                              {a.role === 'super_admin'
                                ? <><Award size={12} aria-hidden="true" /> Super Admin</>
                                : <><Shield size={12} aria-hidden="true" /> Admin</>}
                            </span>
                          </td>
                          <td className={styles.td}>{new Date(a.created_at).toLocaleDateString('id-ID')}</td>
                          <td className={styles.td}>
                            {a.role !== 'super_admin' && a.id !== admin?.id ? (
                              <button className={`${styles.iconBtn} ${styles.deleteBtn}`} onClick={() => handleDeleteAdmin(a.id, a.role)}>
                                <Trash2 size={14} aria-hidden="true" /> Hapus
                              </button>
                            ) : <span className={styles.muted}>—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Panduan menambah admin via Supabase */}
                <div className={styles.guideBox}>
                  <div className={styles.guideTitle}><UserPlus size={16} aria-hidden="true" /> Cara Menambah Admin Baru</div>
                  <div className={styles.guideStep}>
                    <span className={styles.guideNum}>1</span>
                    <span>Minta calon admin mendaftar akun biasa di <span className={styles.guideCode}>/register</span> menggunakan email mereka.</span>
                  </div>
                  <div className={styles.guideStep}>
                    <span className={styles.guideNum}>2</span>
                    <span>Di dashboard Supabase, buka <span className={styles.guideCode}>Authentication › Users</span>, cari email tersebut, lalu salin <span className={styles.guideCode}>User UID</span>-nya.</span>
                  </div>
                  <div className={styles.guideStep}>
                    <span className={styles.guideNum}>3</span>
                    <span>Buka tabel <span className={styles.guideCode}>admins</span>, klik <span className={styles.guideCode}>Insert row</span>, isi <span className={styles.guideCode}>user_id</span> (UID tadi), <span className={styles.guideCode}>name</span>, <span className={styles.guideCode}>email</span>, dan <span className={styles.guideCode}>role</span> (<span className={styles.guideCode}>admin</span> atau <span className={styles.guideCode}>super_admin</span>).</span>
                  </div>
                  <div className={styles.guideStep}>
                    <span className={styles.guideNum}>4</span>
                    <span>Admin baru kini bisa login di <span className={styles.guideCode}>/admin/login</span> memakai email & password mereka.</span>
                  </div>
                </div>
              </div>
            )}

          </>
        )}
      </div>

      {/* MODAL QR */}
      {qrChild && (
        <div className={styles.modalOverlay} onClick={() => setQrChild(null)}>
          <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <div className={styles.modalTitle}>QR Code — {qrChild.name}</div>
                <div className={styles.modalSubtitle}>
                  {qrChild.age_display || `${qrChild.age} thn`} • {qrChild.gender} • {qrChild.diagnosis}
                </div>
              </div>
              <button className={styles.modalClose} onClick={() => setQrChild(null)}>
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div ref={qrCardRef} className={styles.qrCard}>
                <div className={styles.qrCardHeader}>
                  <div className={styles.qrCardLogoRow}>
                    <img src={logo} alt="" className={styles.qrCardLogoImg} />
                    <span className={styles.qrCardLogo}>NOWGUT</span>
                  </div>
                  <div className={styles.qrCardTagline}>Pemantau Risiko Saluran Cerna Anak</div>
                </div>

                <div className={styles.qrCardBody}>
                  <div className={styles.qrCardLeft}>
                    <div className={styles.qrCardName}>{qrChild.name}</div>
                    <div className={styles.qrCardInfo}>Usia: {qrChild.age_display || `${qrChild.age} tahun`}</div>
                    <div className={styles.qrCardInfo}>Jenis Kelamin: {qrChild.gender}</div>
                    <div className={styles.qrCardInfo}>BB: {qrChild.weight_kg} kg • TB: {qrChild.height_cm} cm</div>
                    <div className={styles.qrCardInfo}>Diagnosis: {qrChild.diagnosis}</div>
                  </div>
                  <div className={styles.qrCodeBox}>
                    <QRCodeSVG value={getChildUrl(qrChild.id)} size={130} fgColor="#0a5252" bgColor="#FFFFFF" level="M" />
                  </div>
                </div>

                <div className={styles.qrCardFooter}>
                  <span>Scan untuk data lengkap & riwayat</span>
                  <span className={styles.qrCardFooterRight}>{window.location.host}</span>
                </div>
              </div>

              <div className={styles.qrActions}>
                <button className={styles.qrDownloadBtn} onClick={() => downloadQR(qrChild)}>
                  <Download size={16} aria-hidden="true" /> Download QR Card
                </button>
                <a href={getChildUrl(qrChild.id)} target="_blank" rel="noreferrer" className={styles.qrLinkBtn}>
                  <ExternalLink size={16} aria-hidden="true" /> Buka Halaman
                </a>
              </div>
              <p className={styles.qrNote}>
                Siapa saja yang memiliki QR ini dapat melihat data anak. Gunakan hanya saat konsultasi medis.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;